/* eslint-disable camelcase */
// Service for ONM Design using localStorage instead of API

const STORAGE_KEY = 'onm_designs';
const CURRENT_DESIGN_KEY = 'onm_design_current';

// Helper function to get all designs from localStorage
const getAllDesignsFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading designs from localStorage:', error);
    return [];
  }
};

// Helper function to save all designs to localStorage
const saveAllDesignsToStorage = (designs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(designs));
    return true;
  } catch (error) {
    console.error('Error saving designs to localStorage:', error);
    return false;
  }
};

// Helper function to generate ID
const generateId = () => {
  return `design_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const getDesigns = (query) => {
  return new Promise((resolve, reject) => {
    try {
      const {
        currentPage = 1,
        itemsPerPage = 20,
        sortBy = 'created_at',
        sortOrder = -1,
        search = '',
        ...filterParams
      } = query || {};

      let allDesigns = getAllDesignsFromStorage();

      // Filter by search
      if (search && search.trim()) {
        const searchLower = search.toLowerCase().trim();
        allDesigns = allDesigns.filter(design => {
          const nameMatch = design.name?.toLowerCase().includes(searchLower);
          const descMatch = design.description?.toLowerCase().includes(searchLower);
          const creatorMatch = design.created_by?.toLowerCase().includes(searchLower);
          return nameMatch || descMatch || creatorMatch;
        });
      }

      // Apply other filters
      Object.keys(filterParams).forEach(key => {
        const value = filterParams[key];
        if (value !== undefined && value !== null && value !== '') {
          allDesigns = allDesigns.filter(design => {
            return design[key] === value || String(design[key]).includes(String(value));
          });
        }
      });

      // Sort
      allDesigns.sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];

        // Handle dates
        if (sortBy.includes('date') || sortBy.includes('created_at') || sortBy.includes('updated_at')) {
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
        }

        // Handle strings
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
        }
        if (typeof bVal === 'string') {
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return sortOrder === 1 ? -1 : 1;
        if (aVal > bVal) return sortOrder === 1 ? 1 : -1;
        return 0;
      });

      // Pagination
      const page = parseInt(currentPage) || 1;
      const limit = parseInt(itemsPerPage) || 20;
      const skip = (page - 1) * limit;
      const paginatedDesigns = allDesigns.slice(skip, skip + limit);

      const response = {
        data: {
          data: paginatedDesigns,
          pagination: {
            total_count: allDesigns.length,
            current_page: page,
            items_per_page: limit,
            total_pages: Math.ceil(allDesigns.length / limit)
          }
        },
        success: true
      };

      resolve(response);
    } catch (error) {
      reject(error);
    }
  });
};

export const getDesignByID = (id) => {
  return new Promise((resolve, reject) => {
    try {
      const allDesigns = getAllDesignsFromStorage();
      const design = allDesigns.find(d => d.id === id);

      if (design) {
        resolve({
          data: design,
          success: true
        });
      } else {
        reject(new Error('Design not found'));
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const createDesign = (designData) => {
  return new Promise((resolve, reject) => {
    try {
      const allDesigns = getAllDesignsFromStorage();
      
      const newDesign = {
        ...designData,
        id: designData.id || generateId(),
        created_at: designData.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      allDesigns.push(newDesign);
      saveAllDesignsToStorage(allDesigns);

      resolve({
        data: newDesign,
        success: true
      });
    } catch (error) {
      reject(error);
    }
  });
};

export const updateDesign = (id, designData) => {
  return new Promise((resolve, reject) => {
    try {
      const allDesigns = getAllDesignsFromStorage();
      const index = allDesigns.findIndex(d => d.id === id);

      if (index !== -1) {
        const updatedDesign = {
          ...allDesigns[index],
          ...designData,
          id: id, // Preserve ID
          updated_at: new Date().toISOString()
        };

        allDesigns[index] = updatedDesign;
        saveAllDesignsToStorage(allDesigns);

        resolve({
          data: updatedDesign,
          success: true
        });
      } else {
        reject(new Error('Design not found'));
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteDesign = (id) => {
  return new Promise((resolve, reject) => {
    try {
      const allDesigns = getAllDesignsFromStorage();
      const filteredDesigns = allDesigns.filter(d => d.id !== id);

      if (filteredDesigns.length < allDesigns.length) {
        saveAllDesignsToStorage(filteredDesigns);
        resolve({
          data: { success: true },
          success: true
        });
      } else {
        reject(new Error('Design not found'));
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const tkt1OnmDesignService = {
  getDesigns,
  getDesignByID,
  createDesign,
  updateDesign,
  deleteDesign
};

